import {
  Alert,
  Button,
  Checkbox,
  Modal,
  NumberInput,
  Select,
  Stepper,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { DayInput } from '../../inputs/DayInput.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { TablePager } from '../purchasing/table.js';
import { useLibraryErrorText } from './LibraryTables.js';
import classes from './PmsLibrary.module.css';
import { type PmsLibrarySource, planOfError } from './source.js';
import type { ApplicationPlan, ApplicationPreview, LibraryVessel, PmsLibrary } from './types.js';
import {
  CALENDAR_UNITS,
  type CalendarUnit,
  contentOf,
  emptyWizard,
  filterItems,
  INTERVAL_BASES,
  PAGE_SIZE,
  pageOf,
  pagesOf,
  selectedJobs,
  type WizardItem,
  type WizardJob,
  type WizardState,
  wizardItem,
  wizardJob,
} from './view.js';

const UNKNOWN_ITEM: WizardItem = {
  selected: false,
  code: '',
  name: '',
  tag: '',
  location: '',
  serialNo: '',
  critical: false,
  hasCounter: false,
};
const UNKNOWN_JOB: WizardJob = {
  selected: false,
  every: '',
  unit: 'month',
  hours: '',
  basis: '',
  baselineOn: '',
  baselineHours: '',
};

export interface ApplyWizardProps {
  opened: boolean;
  library: PmsLibrary;
  vessels: LibraryVessel[];
  source: PmsLibrarySource;
  /** Gemi yerel günü (başlangıç günü gelecekte olamaz). */
  today: string;
  onClose: () => void;
  onDone: (message: string) => Promise<void>;
}

/**
 * Apply to Vessel (ADR 0063 §6): gemi → ekipman (seçim = Installed, kod, ad, Tag, Location,
 * Critical, sayaç) → işler ve aralıklar → gemi ağacına göre inceleme → uygula (kurulum devri) ya
 * da gemiye gönder (etkin gemi). Aralığı olmayan iş gemiye inmez.
 */
export function ApplyWizard({
  opened,
  library,
  vessels,
  source,
  today,
  onClose,
  onDone,
}: ApplyWizardProps) {
  const { t } = useTranslation();
  const narrow = useMediaQuery('(max-width: 767px)');
  const errorText = useLibraryErrorText();
  const [step, setStep] = useState(0);
  const [state, setState] = useState<WizardState>(() => emptyWizard());
  const [preview, setPreview] = useState<ApplicationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setStep(0);
    setState(emptyWizard(vessels.length === 1 ? (vessels[0]?.id ?? '') : ''));
    setPreview(null);
    setError(null);
  }, [opened, vessels]);

  const itemIn = (s: WizardState, id: string): WizardItem => {
    const saved = s.items[id];
    if (saved) return saved;
    const item = library.items.find((i) => i.id === id);
    return item ? wizardItem(item) : { ...UNKNOWN_ITEM };
  };
  const jobIn = (s: WizardState, id: string): WizardJob => {
    const saved = s.jobs[id];
    if (saved) return saved;
    const job = library.jobs.find((j) => j.id === id);
    return job ? wizardJob(job) : { ...UNKNOWN_JOB };
  };
  const itemOf = (id: string) => itemIn(state, id);
  const jobOf = (id: string) => jobIn(state, id);
  // Güncel durumun üstüne: art arda iki değişiklikte ilki kaybolmaz
  const setItem = (id: string, patch: Partial<WizardItem>) =>
    setState((s) => ({ ...s, items: { ...s.items, [id]: { ...itemIn(s, id), ...patch } } }));
  const setJob = (id: string, patch: Partial<WizardJob>) =>
    setState((s) => ({ ...s, jobs: { ...s.jobs, [id]: { ...jobIn(s, id), ...patch } } }));

  const content = useMemo(() => contentOf(library, state), [library, state]);
  const chosenJobs = useMemo(() => selectedJobs(library, state), [library, state]);

  const review = async () => {
    setBusy(true);
    setError(null);
    try {
      setPreview(await source.previewApplication(state.vesselId, content));
      setStep(3);
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await source.submitApplication(
        state.vesselId,
        content,
        state.note.trim() || null,
      );
      await onDone(
        outcome.status === 'applied'
          ? t('pmsLibrary.wizard.applied')
          : outcome.status === 'queued'
            ? t('pmsLibrary.wizard.queued', { count: outcome.changes })
            : t('pmsLibrary.wizard.sent'),
      );
      onClose();
    } catch (failure) {
      const plan = planOfError(failure);
      if (plan && preview) setPreview({ ...preview, plan });
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  };

  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pmsLibrary.wizard.${key}`, values);
  const canNext =
    (step === 0 && state.vesselId !== '') || (step === 1 && content.items.length > 0) || step === 2;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={k('title')}
      size="min(1200px, 96vw)"
      fullScreen={narrow}
      closeButtonProps={{ 'aria-label': t('pmsLibrary.actions.close') }}
    >
      <div className={classes.wizard}>
        <Stepper active={step} onStepClick={(next) => next < step && setStep(next)} size="sm">
          <Stepper.Step label={k('steps.vessel')}>
            <VesselStep
              vessels={vessels}
              state={state}
              onChange={(patch) => setState((s) => ({ ...s, ...patch }))}
            />
          </Stepper.Step>
          <Stepper.Step label={k('steps.equipment')}>
            <EquipmentStep
              library={library}
              state={state}
              itemOf={itemOf}
              setItem={setItem}
              setState={setState}
            />
          </Stepper.Step>
          <Stepper.Step label={k('steps.jobs')}>
            <JobsStep library={library} state={state} jobOf={jobOf} setJob={setJob} today={today} />
          </Stepper.Step>
          <Stepper.Step label={k('steps.review')}>
            {preview ? <ReviewStep preview={preview} library={library} /> : null}
          </Stepper.Step>
        </Stepper>
        {error ? <Alert color="red">{error}</Alert> : null}
        <div className={classes.wizardFooter}>
          <Button variant="default" onClick={step === 0 ? onClose : () => setStep(step - 1)}>
            {step === 0 ? t('pmsLibrary.actions.cancel') : t('pmsLibrary.actions.back')}
          </Button>
          <span className={classes.muted}>
            {k('selectedItems', { count: content.items.length })} ·{' '}
            {k('selectedJobs', { count: chosenJobs.length })}
          </span>
          {step < 2 ? (
            <Button disabled={!canNext} onClick={() => setStep(step + 1)}>
              {t('pmsLibrary.actions.next')}
            </Button>
          ) : step === 2 ? (
            <Button
              loading={busy}
              disabled={content.items.length === 0}
              onClick={() => void review()}
            >
              {t('pmsLibrary.actions.review')}
            </Button>
          ) : (
            <Button
              loading={busy}
              disabled={!preview || preview.plan.errors.length > 0}
              onClick={() => void submit()}
            >
              {preview && preview.mode !== 'apply'
                ? t('pmsLibrary.actions.send')
                : t('pmsLibrary.actions.applyNow')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function VesselStep({
  vessels,
  state,
  onChange,
}: {
  vessels: LibraryVessel[];
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pmsLibrary.wizard.${key}`);
  return (
    <div className={classes.wizard}>
      <Alert color="blue">{k('vesselHint')}</Alert>
      <div className={classes.wizardRow}>
        <Select
          label={k('vessel')}
          placeholder={k('vesselPlaceholder')}
          data={vessels.map((v) => ({ value: v.id, label: v.name }))}
          value={state.vesselId || null}
          onChange={(value) => onChange({ vesselId: value ?? '' })}
          searchable
        />
        <Textarea
          label={k('note')}
          autosize
          minRows={1}
          maxLength={1000}
          value={state.note}
          onChange={(event) => onChange({ note: event.currentTarget.value })}
        />
      </div>
    </div>
  );
}

function EquipmentStep({
  library,
  state,
  itemOf,
  setItem,
  setState,
}: {
  library: PmsLibrary;
  state: WizardState;
  itemOf: (id: string) => WizardItem;
  setItem: (id: string, patch: Partial<WizardItem>) => void;
  setState: (update: (s: WizardState) => WizardState) => void;
}) {
  const { t } = useTranslation();
  const [systemId, setSystemId] = useState<string | null>(library.systems[0]?.id ?? null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const rows = filterItems(library, { systemId, query });
  const pages = pagesOf(rows.length);
  const shown = pageOf(rows, Math.min(page, pages));
  const system = library.systems.find((s) => s.id === systemId);
  const k = (key: string) => t(`pmsLibrary.columns.${key}`);
  const allChecked = shown.length > 0 && shown.every((item) => itemOf(item.id).selected);
  return (
    <div className={classes.wizard}>
      <div className={classes.toolbar} style={{ padding: 0, border: 0 }}>
        <Select
          className={classes.systemFilter}
          label={t('pmsLibrary.filters.system')}
          data={library.systems.map((s) => ({ value: s.id, label: `${s.code} ${s.name}` }))}
          value={systemId}
          onChange={(value) => {
            setSystemId(value);
            setPage(1);
          }}
          clearable
          placeholder={t('pmsLibrary.filters.allSystems')}
        />
        {system ? (
          <TextInput
            className={classes.filter}
            label={t('pmsLibrary.wizard.groupCode')}
            value={state.groupCodes[system.id] ?? system.code}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setState((s) => ({ ...s, groupCodes: { ...s.groupCodes, [system.id]: value } }));
            }}
          />
        ) : null}
        <TextInput
          className={classes.search}
          aria-label={t('pmsLibrary.filters.search')}
          placeholder={t('pmsLibrary.filters.search')}
          leftSection={<IconSearch size={16} aria-hidden />}
          value={query}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
            setPage(1);
          }}
        />
      </div>
      {shown.length === 0 ? (
        <EmptyState title={t('pmsLibrary.empty.items')} />
      ) : (
        <section className={table.tableWrap} aria-label={t('pmsLibrary.wizard.steps.equipment')}>
          <table className={table.table}>
            <thead>
              <tr>
                <th scope="col" className={classes.colCheck}>
                  <Checkbox
                    aria-label={t('pmsLibrary.actions.selectAll')}
                    checked={allChecked}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      for (const item of shown) setItem(item.id, { selected: checked });
                    }}
                  />
                </th>
                <th scope="col" className={classes.colCodeInput}>
                  {k('code')}
                </th>
                <th scope="col">{k('name')}</th>
                <th scope="col">{k('tag')}</th>
                <th scope="col">{k('location')}</th>
                <th scope="col">{k('serialNo')}</th>
                <th scope="col" className={classes.colFlag}>
                  {k('critical')}
                </th>
                <th scope="col" className={classes.colFlag}>
                  {k('counter')}
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((item) => {
                const w = itemOf(item.id);
                return (
                  <tr key={item.id}>
                    <td>
                      <Checkbox
                        aria-label={`${k('select')} ${item.code}`}
                        checked={w.selected}
                        onChange={(event) =>
                          setItem(item.id, { selected: event.currentTarget.checked })
                        }
                      />
                    </td>
                    <td>
                      <TextInput
                        size="xs"
                        aria-label={`${k('code')} ${item.code}`}
                        value={w.code}
                        onChange={(event) => setItem(item.id, { code: event.currentTarget.value })}
                      />
                    </td>
                    <td title={item.components ?? item.name}>
                      <TextInput
                        size="xs"
                        aria-label={`${k('name')} ${item.code}`}
                        value={w.name}
                        onChange={(event) => setItem(item.id, { name: event.currentTarget.value })}
                      />
                    </td>
                    <td>
                      <TextInput
                        size="xs"
                        aria-label={`${k('tag')} ${item.code}`}
                        value={w.tag}
                        maxLength={40}
                        onChange={(event) => setItem(item.id, { tag: event.currentTarget.value })}
                      />
                    </td>
                    <td>
                      <TextInput
                        size="xs"
                        aria-label={`${k('location')} ${item.code}`}
                        value={w.location}
                        maxLength={120}
                        onChange={(event) =>
                          setItem(item.id, { location: event.currentTarget.value })
                        }
                      />
                    </td>
                    <td>
                      <TextInput
                        size="xs"
                        aria-label={`${k('serialNo')} ${item.code}`}
                        value={w.serialNo}
                        maxLength={80}
                        onChange={(event) =>
                          setItem(item.id, { serialNo: event.currentTarget.value })
                        }
                      />
                    </td>
                    <td>
                      <Checkbox
                        aria-label={`${k('critical')} ${item.code}`}
                        checked={w.critical}
                        onChange={(event) =>
                          setItem(item.id, { critical: event.currentTarget.checked })
                        }
                      />
                    </td>
                    <td>
                      <Checkbox
                        aria-label={`${k('counter')} ${item.code}`}
                        checked={w.hasCounter}
                        onChange={(event) =>
                          setItem(item.id, { hasCounter: event.currentTarget.checked })
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
      <TablePager
        page={Math.min(page, pages)}
        pages={pages}
        summary={t('pmsLibrary.showing', {
          from: rows.length === 0 ? 0 : (Math.min(page, pages) - 1) * PAGE_SIZE + 1,
          to: Math.min(rows.length, Math.min(page, pages) * PAGE_SIZE),
          total: rows.length,
        })}
        label={t('pmsLibrary.wizard.steps.equipment')}
        onPage={setPage}
      />
    </div>
  );
}

function JobsStep({
  library,
  state,
  jobOf,
  setJob,
  today,
}: {
  library: PmsLibrary;
  state: WizardState;
  jobOf: (id: string) => WizardJob;
  setJob: (id: string, patch: Partial<WizardJob>) => void;
  today: string;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const rows = library.jobs.filter((job) => state.items[job.itemId]?.selected === true);
  const pages = pagesOf(rows.length);
  const shown = pageOf(rows, Math.min(page, pages));
  const k = (key: string) => t(`pmsLibrary.columns.${key}`);
  return (
    <div className={classes.wizard}>
      <Alert color="blue">{t('pmsLibrary.wizard.jobsHint')}</Alert>
      {shown.length === 0 ? (
        <EmptyState title={t('pmsLibrary.wizard.noJobs')} />
      ) : (
        <section className={table.tableWrap} aria-label={t('pmsLibrary.wizard.steps.jobs')}>
          <table className={table.table}>
            <thead>
              <tr>
                <th scope="col" className={classes.colCheck}>
                  <span className={classes.visuallyHidden}>{k('select')}</span>
                </th>
                <th scope="col" className={classes.colJobCode}>
                  {k('jobCode')}
                </th>
                <th scope="col">{k('title')}</th>
                <th scope="col" className={classes.colNarrow}>
                  {k('every')}
                </th>
                <th scope="col" className={classes.colStatus}>
                  {k('unit')}
                </th>
                <th scope="col" className={classes.colStatus}>
                  {k('hours')}
                </th>
                <th scope="col" className={classes.colStatus}>
                  {k('basis')}
                </th>
                <th scope="col" className={classes.colStatus}>
                  {k('baselineOn')}
                </th>
                <th scope="col" className={classes.colStatus}>
                  {k('baselineHours')}
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((job) => {
                const w = jobOf(job.id);
                const hoursSet = w.hours !== '';
                return (
                  <tr key={job.id}>
                    <td>
                      <Checkbox
                        aria-label={`${k('select')} ${job.code}`}
                        checked={w.selected}
                        onChange={(event) =>
                          setJob(job.id, { selected: event.currentTarget.checked })
                        }
                      />
                    </td>
                    <td>
                      {job.code}
                      {job.status === 'approved' ? (
                        <>
                          {' '}
                          <StatusBadge tone="success">
                            {t('pmsLibrary.statuses.approved')}
                          </StatusBadge>
                        </>
                      ) : null}
                    </td>
                    <td className={classes.wrap} title={job.title}>
                      {job.title}
                    </td>
                    <td>
                      <NumberInput
                        size="xs"
                        aria-label={`${k('every')} ${job.code}`}
                        min={1}
                        max={1000}
                        allowDecimal={false}
                        value={w.every}
                        onChange={(value) =>
                          setJob(job.id, { every: value === '' ? '' : Number(value) })
                        }
                      />
                    </td>
                    <td>
                      <Select
                        size="xs"
                        aria-label={`${k('unit')} ${job.code}`}
                        allowDeselect={false}
                        data={CALENDAR_UNITS.map((u) => ({
                          value: u,
                          label: t(`pmsLibrary.unitNames.${u}`),
                        }))}
                        value={w.unit}
                        onChange={(value) =>
                          setJob(job.id, { unit: (value ?? 'month') as CalendarUnit })
                        }
                      />
                    </td>
                    <td>
                      <NumberInput
                        size="xs"
                        aria-label={`${k('hours')} ${job.code}`}
                        min={1}
                        max={1_000_000}
                        decimalScale={1}
                        value={w.hours}
                        onChange={(value) =>
                          setJob(job.id, { hours: value === '' ? '' : Number(value) })
                        }
                      />
                    </td>
                    <td>
                      <Select
                        size="xs"
                        aria-label={`${k('basis')} ${job.code}`}
                        clearable
                        // Seçili seçeneğe tıklamak seçimi kaldırmaz; kaldırma × düğmesiyle
                        allowDeselect={false}
                        data={INTERVAL_BASES.map((b) => ({
                          value: b,
                          label: t(`pmsLibrary.bases.${b}`),
                        }))}
                        value={w.basis || null}
                        onChange={(value) => setJob(job.id, { basis: value ?? '' })}
                      />
                    </td>
                    <td>
                      {hoursSet ? (
                        <DayInput
                          size="xs"
                          aria-label={`${k('baselineOn')} ${job.code}`}
                          value={w.baselineOn || null}
                          maxDate={today}
                          onChange={(value) => setJob(job.id, { baselineOn: value ?? '' })}
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {hoursSet ? (
                        <NumberInput
                          size="xs"
                          aria-label={`${k('baselineHours')} ${job.code}`}
                          min={0}
                          decimalScale={1}
                          value={w.baselineHours}
                          onChange={(value) =>
                            setJob(job.id, { baselineHours: value === '' ? '' : Number(value) })
                          }
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
      <TablePager
        page={Math.min(page, pages)}
        pages={pages}
        summary={t('pmsLibrary.showing', {
          from: rows.length === 0 ? 0 : (Math.min(page, pages) - 1) * PAGE_SIZE + 1,
          to: Math.min(rows.length, Math.min(page, pages) * PAGE_SIZE),
          total: rows.length,
        })}
        label={t('pmsLibrary.wizard.steps.jobs')}
        onPage={setPage}
      />
    </div>
  );
}

function ReviewStep({ preview, library }: { preview: ApplicationPreview; library: PmsLibrary }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pmsLibrary.wizard.${key}`);
  const plan: ApplicationPlan = preview.plan;
  const names = new Map<string, string>([
    ...library.systems.map((s) => [s.id, `${s.code} ${s.name}`] as const),
    ...library.items.map((i) => [i.id, `${i.code} ${i.subSystem ?? i.name}`] as const),
    ...library.jobs.map((j) => [j.id, `${j.code} ${j.title}`] as const),
  ]);
  const summaryKeys = [
    'groupsCreated',
    'groupsExisting',
    'itemsCreated',
    'itemsAdopted',
    'itemsExisting',
    'jobsCreated',
    'jobsExisting',
  ] as const;
  return (
    <div className={classes.wizard}>
      <Alert color={preview.mode === 'apply' ? 'blue' : 'yellow'}>
        {preview.mode === 'send'
          ? k('modeSend')
          : preview.mode === 'queue'
            ? k('modeQueue')
            : k('modeApply')}{' '}
        {k('reviewHint')}
      </Alert>
      <section className={classes.reviewGrid} aria-label={k('steps.review')}>
        {summaryKeys.map((key) => (
          <div key={key} className={classes.reviewCell}>
            <span className={classes.muted}>{t(`pmsLibrary.wizard.summary.${key}`)}</span>
            <span className={classes.reviewValue}>{plan.summary[key]}</span>
          </div>
        ))}
      </section>
      {plan.errors.length === 0 ? (
        <Alert color="green">{k('noErrors')}</Alert>
      ) : (
        <Alert color="red" title={k('errorsTitle')}>
          <ul className={classes.list}>
            {plan.errors.map((e) => (
              <li key={`${e.kind}-${e.libraryId}-${e.code}`}>
                <strong>{names.get(e.libraryId) ?? e.libraryId}</strong>:{' '}
                {t(`pmsLibrary.applyErrors.${e.code}`, {
                  defaultValue: t(`pms.errors.${e.code}`, { defaultValue: e.code }),
                })}
              </li>
            ))}
          </ul>
        </Alert>
      )}
    </div>
  );
}
